import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { clipCollectionQueryOptions } from '../api/collectionQueries';
import type { CollectionClient, CollectionPage } from '../model/clipCollection';

/** The next page of a wall: whether there is one, its wait, its failure, and the ask. */
export interface OlderPages {
  readonly exists: boolean;
  readonly pending: boolean;
  readonly failed: boolean;
  readonly load: () => void;
}

export interface ClipCollection {
  /** Every page that has landed, in the backend's order; the previous narrowing's while `stale`. */
  readonly pages: readonly CollectionPage[];
  /** The first page's clients — the whole collection, whatever the read is narrowed to. */
  readonly clients: readonly CollectionClient[] | null;
  /** No answer at all yet, or a failed first read being asked again. */
  readonly pending: boolean;
  /** What is held is the previous narrowing's answer while this one's first page is read. */
  readonly stale: boolean;
  /** The first read failed with nothing on screen. A failed refetch or older page does not count. */
  readonly failed: boolean;
  readonly error: unknown;
  readonly retry: () => void;
  readonly older: OlderPages;
}

/**
 * `GET /trainer/clips` (R27) as one wall reads it: every client's recordings (`customerId` null) or
 * one client's. The previous narrowing is held as placeholder data so a surface can keep what does
 * not depend on the narrowing (the client list) standing; the wall itself waits on `stale`.
 *
 * `enabled: false` leaves a disabled query `isPending` for ever, so a caller gates its wait on its
 * own enablement too.
 */
export function useClipCollection(customerId: number | null, enabled = true): ClipCollection {
  const lang = useContentLanguage();
  const query = useInfiniteQuery({
    ...clipCollectionQueryOptions(customerId, lang),
    placeholderData: keepPreviousData,
    enabled,
  });
  const { fetchNextPage, refetch } = query;
  const failed = query.isError && query.data === undefined;

  return {
    pages: query.data?.pages ?? [],
    clients: query.data?.pages[0]?.clients ?? null,
    pending: query.isPending || (failed && query.isFetching),
    stale: query.isPlaceholderData,
    failed,
    error: query.error,
    retry: () => {
      void refetch();
    },
    older: {
      exists: query.hasNextPage,
      pending: query.isFetchingNextPage,
      failed: query.isFetchNextPageError,
      // `cancelRefetch: false` makes a second ask while one is in flight join it rather than restart it.
      load: () => {
        void fetchNextPage({ cancelRefetch: false });
      },
    },
  };
}
